import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowRight, FileText } from "lucide-react";
import { PHOTOS, LEAFLET_URL } from "./data";
import { openBooklet } from "./BookletFlipbook";

const ease = [0.16, 1, 0.3, 1];

const Line = ({ children, delay, italic }) => (
  <div className="overflow-hidden pb-[0.12em]">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease }}
      className={`block leading-[1.14] pb-[0.06em] ${italic ? "italic text-terra" : ""}`}
    >
      {children}
    </motion.span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const [count, setCount] = useState(0);
  useEffect(() => {
    const dur = 1600;
    let raf;
    const start = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const prog = Math.min((now - t0) / dur, 1);
        const eased = 1 - Math.pow(1 - prog, 3);
        setCount(Math.round(eased * 400));
        if (prog < 1) raf = requestAnimationFrame(tick);
        else setCount(400);
      };
      raf = requestAnimationFrame(tick);
    };
    const id = setTimeout(start, 1300);
    return () => { clearTimeout(id); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden" data-testid="hero">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-xs md:text-sm tracking-[0.22em] uppercase font-semibold text-terra mb-6"
          >
            Evidence-Led · Inclusive · Person-Centred
          </motion.p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-[6.5rem] tracking-tight">
            <Line delay={0.15}>Potential</Line>
            <Line delay={0.25}>beyond</Line>
            <Line delay={0.35}>challenges,</Line>
            <Line delay={0.45} italic>always.</Line>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease }}
            className="mt-8 max-w-lg text-base md:text-lg leading-relaxed text-ink/70"
          >
            We partner with schools to build high-performing learning communities through evidence-based
            solutions that create meaningful, sustainable change.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-cta"
              className="group bg-terra text-white px-8 py-4 text-sm tracking-wide flex items-center gap-3 hover:shadow-[6px_6px_0_0_#14283D] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all duration-300"
            >
              Book a Free Consultation Today
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              type="button"
              onClick={openBooklet}
              data-testid="hero-overview-pdf"
              className="group bg-pine text-bone px-7 py-4 text-sm tracking-wide flex items-center gap-2.5 hover:bg-ink transition-colors duration-300"
            >
              <FileText className="w-4 h-4" /> Read our Workshops Booklet
            </button>
            <a
              href={LEAFLET_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-leaflet-pdf"
              className="group border border-ink/25 text-ink px-7 py-4 text-sm tracking-wide flex items-center gap-2.5 hover:border-terra hover:text-terra transition-colors duration-300"
            >
              <FileText className="w-4 h-4" /> Read our Inclusion Support Leaflet
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: imgY }} className="relative aspect-[3/4] w-full overflow-hidden">
            <div className="h-full w-full -scale-x-100">
              <motion.img
                initial={{ scale: 1.25 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 0.4, ease }}
                src={PHOTOS.assembly}
                alt="The People's Josh working with educators in a school"
                className="w-full h-full object-cover object-[70%_50%]"
              />
            </div>
            <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
          </motion.div>
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.3, ease }}
            className="absolute -left-4 md:-left-10 bottom-24 md:bottom-28 bg-pine text-bone p-5 w-[240px] md:w-[280px] pointer-events-none"
          >
            <span className="font-serif text-4xl leading-none">{count}+</span>
            <p className="text-xs mt-2 text-bone/80 tracking-wide">learners reached across partner schools</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
